import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../../components/values/Get';
import { getWhatHasBeenDamaged, getWorkshopDetails } from '../shared/vehicle';
import { isCustomerAllowedToSelectWorkshop } from '../../helpers/business-rules/isCustomerAllowedToSelectWorkshop';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';

    switch (curPageId) {
        // Step 0: Landing page
        case 'godt-at-vide':
            nextPage = 'dato';
            break;

        // Step 1: About the damage
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'koeretoej-nummerplade';
            break;

        case 'koeretoej-nummerplade':
            nextPage = 'hvilket-koeretoej';
            break;
        case 'hvilket-koeretoej':
            nextPage = 'hvad-er-skadet';
            break;
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;

        case 'skadezone':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;

        case 'skade-andre-koertoejer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);

            break;
        case 'anden-persons-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skade-ting':
            nextPage = 'foerer';

            const isReadyToFileAllThingsDamaged =
                getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes';
            if (isReadyToFileAllThingsDamaged) {
                nextPage = 'genstandsregistrering';
            }

            break;
        case 'genstandsregistrering':
            nextPage = 'foerer';
            break;

        // Step 2: About the driver
        case 'foerer':
            nextPage = 'paavirkning';

            const selectedDriver = getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value;
            const driverOptionsWhereExtraInfoIsRequired = ['aEmployee', 'aRenter', 'other'];

            if (driverOptionsWhereExtraInfoIsRequired.includes(selectedDriver)) {
                nextPage = 'foerer-oplysninger';
            }

            break;
        case 'foerer-oplysninger':
            nextPage = 'paavirkning';
            break;
        case 'paavirkning':
            nextPage = 'oplysninger-om-modpart';
            break;

        // Step 3: Counterpart
        case 'oplysninger-om-modpart':
            nextPage = 'dokumentation';

            if (getValueOrDefault(formConfig, formValues, 'doYouHaveInformationAboutOthersInvolved').value === 'yes') {
                nextPage = 'modpartens-rolle-1';
            }

            break;
        // Page 9A: Counterpart (counterparts role, person 1-4)
        case 'modpartens-rolle-1':
            nextPage = 'om-modparten-1';
            break;
        case 'modpartens-rolle-2':
            nextPage = 'om-modparten-2';
            break;
        case 'modpartens-rolle-3':
            nextPage = 'om-modparten-3';
            break;
        // Page 9B: Counterpart (details, person 1-4)
        case 'om-modparten-1':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson2',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-2'
                : 'dokumentation';
            break;
        case 'om-modparten-2':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson3',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-3'
                : 'dokumentation';
            break;
        case 'om-modparten-3':
            nextPage = 'dokumentation';
            break;

        // Step 4: Documentation
        case 'dokumentation':
            nextPage = 'politi';
            break;

        case 'politi':
            if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cvr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'virksomhedsoplysninger';
            }

            break;
        case 'find-vaerksted':
            nextPage = 'virksomhedsoplysninger';
            break;
        case 'virksomhedsoplysninger':
            nextPage = 'om-virksomheden';
            break;
        case 'om-virksomheden':
            nextPage = 'om-dig';
            break;
        case 'om-dig':
            nextPage = 'resume';
            break;
        case 'resume':
            nextPage = undefined;
            break;
        default:
            console.log('# curPageId=', curPageId);
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Decides which damage page that are the next to show, depending on where in the form flow the user is, and which damage checkboxes that have been selected.
 * @param currentPageId The id of the current page
 * @param formConfig
 * @param formValues
 * @returns Next damage page to show.
 */
const getNextDamagePage = (
    currentPageId: string,
    formConfig: FormConfig,
    formValues: FormValues
): string | undefined => {
    const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);

    switch (currentPageId) {
        case 'hvad-er-skadet':
            if (damageSelections.vehicle.isMyVehicleDamaged) {
                const isVehicleTypeOtherSelected =
                    getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other';

                // If a customer has selected 'other' as vehicle type -> skip the skadezone page
                return isVehicleTypeOtherSelected ? 'skader-andet-koeretoej' : 'skadezone';
            }

            if (damageSelections.vehicle.isOthersVehicleDamaged) return 'skade-andre-koertoejer';
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';

        case 'skadezone':
        case 'skader-andet-koeretoej':
            if (damageSelections.vehicle.isOthersVehicleDamaged) return 'skade-andre-koertoejer';
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'skade-andre-koertoejer':
            const isSingleCounterpartVehicleDamaged =
                getValueOrDefault(formConfig, formValues, 'howManyOtherVehiclesAreDamaged').value === 'aSingleVehicle';

            if (isSingleCounterpartVehicleDamaged) {
                return 'anden-persons-koeretoej';
            }

            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'anden-persons-koeretoej':
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'personskade':
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'oplysninger-om-passagerer':
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
    }
};

/**
 * Determines if a given 'add another person' checkbox element is selected or not.
 * @param elementId Id of the 'add another person' checkbox element
 * @param formConfig
 * @param formValues
 * @returns True if the 'add another person' checkbox is selected, else false.
 */
const isAddAnotherPersonChecked = (elementId: string, formConfig: FormConfig, formValues: FormValues): boolean => {
    const addAnotherPersonCheckbox = getValueOrDefault(formConfig, formValues, elementId);
    return addAnotherPersonCheckbox && addAnotherPersonCheckbox.length > 0
        ? addAnotherPersonCheckbox[0].value === 'yes'
        : false;
};
